import { useCallback, useEffect, useState } from 'react'
import { Plus, Trash2 } from 'lucide-react'
import type { BoundaryMapping, KnobRow, ThresholdOverride } from './types'
import {
  createThreshold, deleteBoundaryMapping, fetchBoundaryMap, fetchThresholds, upsertBoundaryMapping,
} from './useAdminApi'
import { SettingsFormModal } from './SettingsFormModal'
import { AdminTable, Banner, Btn, Field, SectionHeader, Td, Th, inputCls } from './ui'


const PLACES = ['DHAR', 'DHULE', 'JHUL']
const PHASES = ['queueing', 'gate', 'loading/unloading', 'yard', 'depot halt', 'tarpaulin']
const EDGES = ['start', 'end']

export function ThresholdsSection({ configured }: { configured: boolean }) {
  const [overrides, setOverrides] = useState<ThresholdOverride[]>([])
  const [knobs, setKnobs] = useState<KnobRow[]>([])
  const [filter, setFilter] = useState('')
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [banner, setBanner] = useState<{ tone: 'ok' | 'error'; msg: string } | null>(null)
  const [form, setForm] = useState({
    knob: 'OVERSPEED_TOLERANCE_KPH', value: '5', scope: 'global', scope_id: '', note: '',
  })
  const [open, setOpen] = useState(false)

  const load = useCallback(async () => {
    if (!configured) { setLoading(false); return }
    setLoading(true)
    setError(null)
    try {
      const data = await fetchThresholds()
      setOverrides(data.overrides)
      setKnobs(data.knobs)
      if (data.knobs[0] && !form.knob) setForm((f) => ({ ...f, knob: data.knobs[0]!.knob }))
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to load thresholds')
    } finally {
      setLoading(false)
    }
  }, [configured, form.knob])

  useEffect(() => { void load() }, [load])

  const save = async () => {
    try {
      const r = await createThreshold({
        knob: form.knob, value: form.value, scope: form.scope,
        scope_id: form.scope === 'global' ? '' : form.scope_id, note: form.note,
      })
      setBanner({ tone: 'ok', msg: `Saved — applies ${r.applies_on}` })
      setOpen(false)
      await load()
    } catch (e) {
      setBanner({ tone: 'error', msg: e instanceof Error ? e.message : 'Save failed' })
    }
  }

  if (!configured) {
    return <p className="text-sm text-slate-500">The reporting database is not configured.</p>
  }

  return (
    <div className="space-y-4">
      <SectionHeader
        title="Alert thresholds"
        subtitle="Live within ~30s. Vehicle beats route beats global. Locked sensor limits cannot be edited."
        action={<Btn onClick={() => setOpen(true)}><span className="inline-flex items-center gap-1"><Plus size={14} /> Add override</span></Btn>}
      />
      {banner ? <Banner tone={banner.tone}>{banner.msg}</Banner> : null}
      {error ? <Banner tone="error">{error}</Banner> : null}
      {loading ? <p className="text-sm text-slate-500">Loading…</p> : (
        <>
          {/* EVERY THRESHOLD, WITH THE VALUE ACTUALLY IN FORCE. This panel used to list only
              the overrides somebody had already created, so it opened empty and said nothing
              about what the fleet was running on -- an operator had to read the source to
              find out that harsh braking was -1.5. The catalogue now comes from the rules'
              own registry, so a knob added to a rule appears here without anybody
              remembering to add it. */}
          <input
            className={`${inputCls} mb-3`}
            placeholder="Filter — speed, harsh, soc, congestion…"
            value={filter}
            onChange={(e) => setFilter(e.target.value)}
          />
          <AdminTable>
            <thead>
              <tr>
                <Th>Threshold</Th><Th>In force</Th><Th>Default</Th><Th>Source</Th><Th>What it does</Th><Th></Th>
              </tr>
            </thead>
            <tbody>
              {knobs
                .filter((k) => !filter
                  || k.knob.toLowerCase().includes(filter.toLowerCase())
                  || (k.label ?? '').toLowerCase().includes(filter.toLowerCase())
                  || k.why.toLowerCase().includes(filter.toLowerCase()))
                .map((k) => {
                  const inForce = k.effective ?? k.value
                  const changed = Number(inForce) !== Number(k.default)
                  return (
                    <tr key={k.knob}>
                      <Td>
                        {/* The human name leads; the environment variable is underneath in
                            mono, because a runbook refers to it by that name and hiding it
                            would make the two impossible to line up. */}
                        <div className="text-t1">{k.label ?? k.knob}</div>
                        <div className="font-mono text-[11px] text-slate-500">{k.knob}</div>
                      </Td>
                      <Td className={`font-mono ${changed ? 'text-amber-400' : ''}`}>
                        {inForce}{k.unit ? ` ${k.unit}` : ''}
                      </Td>
                      <Td className="font-mono text-slate-500">{k.default}</Td>
                      {/* Three different things, and collapsing them would hide which one a
                          reader is looking at: the code default, a fleet-wide deployment
                          setting, and somebody's deliberate override. */}
                      <Td>
                        {k.source === 'override'
                          ? <span title={k.overridden_by ?? ''}>override{k.overridden_by ? ` · ${k.overridden_by}` : ''}</span>
                          : k.source === 'deployment' ? 'deployment' : 'default'}
                      </Td>
                      <Td className="text-slate-400 text-[12px]">{k.why}</Td>
                      <Td>
                        <Btn onClick={() => {
                          setForm({ knob: k.knob, value: String(inForce), scope: 'global', scope_id: '', note: '' })
                          setOpen(true)
                        }}>Edit</Btn>
                      </Td>
                    </tr>
                  )
                })}
            </tbody>
          </AdminTable>

          <p className="mt-6 mb-2 text-xs uppercase tracking-wide text-slate-500">
            Active overrides — vehicle beats route beats global
          </p>
          <AdminTable>
            <thead>
              <tr>
                <Th>Knob</Th><Th>Value</Th><Th>Scope</Th><Th>Who</Th><Th>When</Th>
              </tr>
            </thead>
            <tbody>
              {overrides.length === 0 ? (
                <tr><td colSpan={5} className="px-3 py-8 text-center text-sm text-slate-400">No overrides — every threshold above is on its default.</td></tr>
              ) : overrides.map((o) => (
                <tr key={`${o.knob}-${o.scope}-${o.scope_id ?? ''}-${o.override_id}`}>
                  <Td className="font-mono text-[12px]">{o.knob}</Td>
                  <Td className="font-mono">{o.value}</Td>
                  <Td>{o.scope}{o.scope_id ? ` · ${o.scope_id}` : ''}</Td>
                  <Td>{o.by_whom ?? '—'}</Td>
                  <Td className="text-slate-500">{o.ts ? new Date(o.ts).toLocaleString('en-IN') : '—'}</Td>
                </tr>
              ))}
            </tbody>
          </AdminTable>
        </>
      )}

      <SettingsFormModal open={open} title={`Set ${form.knob}`} onClose={() => setOpen(false)} onSave={() => void save()}>
          <Field label="Knob">
            <select className={inputCls} value={form.knob}
              onChange={(e) => setForm({ ...form, knob: e.target.value })}>
              {knobs.map((k) => (
                <option key={k.knob} value={k.knob}>{k.knob} ({k.unit}) — {k.why}</option>
              ))}
            </select>
          </Field>
          <Field label="Value">
            <input className={inputCls} value={form.value}
              onChange={(e) => setForm({ ...form, value: e.target.value })} />
          </Field>
          <Field label="Scope">
            <select className={inputCls} value={form.scope}
              onChange={(e) => setForm({ ...form, scope: e.target.value })}>
              <option value="global">global</option>
              <option value="route">route</option>
              <option value="vehicle">vehicle</option>
            </select>
          </Field>
          {form.scope !== 'global' && (
            <Field label={form.scope === 'route' ? 'Route id' : 'Vehicle id'}>
              <input className={inputCls} value={form.scope_id}
                onChange={(e) => setForm({ ...form, scope_id: e.target.value })}
                placeholder={form.scope === 'route' ? 'dhar-dhule-laden' : 'MH18BZ3300'} />
            </Field>
          )}
          <Field label="Note">
            <input className={inputCls} value={form.note}
              onChange={(e) => setForm({ ...form, note: e.target.value })} />
          </Field>
        </SettingsFormModal>
    </div>
  )
}

export function BoundaryMapSection({ configured }: { configured: boolean }) {
  const [rows, setRows] = useState<BoundaryMapping[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [banner, setBanner] = useState<{ tone: 'ok' | 'error'; msg: string } | null>(null)
  const [editing, setEditing] = useState<BoundaryMapping | null>(null)
  const [isNew, setIsNew] = useState(false)

  const load = useCallback(async () => {
    if (!configured) { setLoading(false); return }
    setLoading(true)
    setError(null)
    try {
      setRows(await fetchBoundaryMap())
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to load boundary map')
    } finally {
      setLoading(false)
    }
  }, [configured])

  useEffect(() => { void load() }, [load])

  const openNew = () => {
    setEditing({ event_code: '', place: 'DHAR', phase: 'gate', edge: 'start', note: '' })
    setIsNew(true)
  }

  const save = async () => {
    if (!editing?.event_code?.trim()) {
      setBanner({ tone: 'error', msg: 'Event code is required' })
      return
    }
    try {
      await upsertBoundaryMapping(editing)
      setBanner({ tone: 'ok', msg: isNew ? 'Mapping created' : 'Mapping updated' })
      setEditing(null)
      await load()
    } catch (e) {
      setBanner({ tone: 'error', msg: e instanceof Error ? e.message : 'Save failed' })
    }
  }

  const remove = async (m: BoundaryMapping) => {
    if (!window.confirm(`Delete mapping ${m.event_code}?`)) return
    try {
      await deleteBoundaryMapping(m.event_code)
      setBanner({ tone: 'ok', msg: 'Deleted' })
      await load()
    } catch (e) {
      setBanner({ tone: 'error', msg: e instanceof Error ? e.message : 'Delete failed' })
    }
  }

  if (!configured) {
    return <p className="text-sm text-slate-500">The reporting database is not configured.</p>
  }

  return (
    <div className="space-y-4">
      <SectionHeader
        title="TIMS boundary map"
        subtitle="Maps register event codes to hub phases. Manual stages win over geotags when start+end are mapped."
        action={<Btn onClick={openNew}><span className="inline-flex items-center gap-1"><Plus size={14} /> Add mapping</span></Btn>}
      />
      {banner ? <Banner tone={banner.tone}>{banner.msg}</Banner> : null}
      {error ? <Banner tone="error">{error}</Banner> : null}
      {loading ? <p className="text-sm text-slate-500">Loading…</p> : (
        <AdminTable>
          <thead>
            <tr>
              <Th>Event code</Th><Th>Place</Th><Th>Phase</Th><Th>Edge</Th><Th>Note</Th><Th />
            </tr>
          </thead>
          <tbody>
            {rows.map((m) => (
              <tr key={m.event_code}>
                <Td className="font-mono text-[12px]">{m.event_code}</Td>
                <Td>{m.place}</Td>
                <Td>{m.phase}</Td>
                <Td>{m.edge}</Td>
                <Td className="text-slate-500">{m.note ?? '—'}</Td>
                <Td>
                  <button type="button" className="text-slate-400 hover:text-rose-600"
                    onClick={() => void remove(m)} aria-label="delete">
                    <Trash2 size={14} />
                  </button>
                </Td>
              </tr>
            ))}
          </tbody>
        </AdminTable>
      )}

      <SettingsFormModal open={editing !== null}
        title={isNew ? 'Add mapping' : 'Edit mapping'}
        onClose={() => setEditing(null)} onSave={() => void save()}>
        {editing ? (
          <>
            <Field label="Event code">
              <input className={inputCls} value={editing.event_code}
                disabled={!isNew}
                onChange={(e) => setEditing({ ...editing, event_code: e.target.value })}
                placeholder="DHAR_IN" />
            </Field>
            <Field label="Place">
              <select className={inputCls} value={editing.place}
                onChange={(e) => setEditing({ ...editing, place: e.target.value })}>
                {PLACES.map((p) => <option key={p} value={p}>{p}</option>)}
              </select>
            </Field>
            <Field label="Phase">
              <select className={inputCls} value={editing.phase}
                onChange={(e) => setEditing({ ...editing, phase: e.target.value })}>
                {PHASES.map((p) => <option key={p} value={p}>{p}</option>)}
              </select>
            </Field>
            <Field label="Edge">
              <select className={inputCls} value={editing.edge}
                onChange={(e) => setEditing({ ...editing, edge: e.target.value as 'start' | 'end' })}>
                {EDGES.map((p) => <option key={p} value={p}>{p}</option>)}
              </select>
            </Field>
            <Field label="Note">
              <input className={inputCls} value={editing.note ?? ''}
                onChange={(e) => setEditing({ ...editing, note: e.target.value })} />
            </Field>
          </>
        ) : null}
      </SettingsFormModal>
    </div>
  )
}
